/**
 * ANTLITZWERK complimentary-delivery threshold renderer.
 *
 * Keeps the cart messaging in the active presentment currency. Shopify's
 * current storefront conversion rate converts the €49 source threshold, which
 * is then rounded up to the next whole currency unit.
 */
(() => {
  const ELEMENT_NAME = 'aw-shipping-threshold';
  const EPSILON = 1e-8;

  if (!('customElements' in window) || customElements.get(ELEMENT_NAME)) return;

  const toFiniteNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const normaliseCurrency = (value, fallback = 'EUR') => {
    const currency = String(value || fallback).trim().toUpperCase();
    return /^[A-Z]{3}$/.test(currency) ? currency : fallback;
  };

  const getCurrencyPrecision = (locale, currency) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).resolvedOptions().maximumFractionDigits;
    } catch (_error) {
      return 2;
    }
  };

  const formatCurrency = (amount, locale, currency, wholeUnitsOnly = false) => {
    const precision = wholeUnitsOnly ? 0 : getCurrencyPrecision(locale, currency);
    const scale = 10 ** precision;
    const roundedAmount = precision > 0 ? Math.round((amount + EPSILON) * scale) / scale : Math.round(amount);
    const hasFraction = precision > 0 && Math.abs(roundedAmount - Math.round(roundedAmount)) > EPSILON;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: wholeUnitsOnly || !hasFraction ? 0 : precision,
        maximumFractionDigits: precision,
      }).format(roundedAmount);
    } catch (_error) {
      const fallbackAmount = wholeUnitsOnly ? Math.round(roundedAmount) : roundedAmount;
      return `${fallbackAmount} ${currency}`;
    }
  };

  class AwShippingThreshold extends HTMLElement {
    #renderQueued = false;

    static get observedAttributes() {
      return [
        'data-aw-base-threshold',
        'data-aw-cart-total',
        'data-aw-currency',
        'data-aw-fallback-threshold',
        'data-aw-locale',
        'data-aw-mode',
        'data-aw-source-currency',
        'data-aw-store-currency',
      ];
    }

    connectedCallback() {
      this.queueRender();
    }

    attributeChangedCallback(_name, oldValue, newValue) {
      if (oldValue !== newValue && this.isConnected) this.queueRender();
    }

    /**
     * Cart sections are morphed synchronously. Rendering in a microtask ensures
     * the resolved currency value is applied after both attributes and child
     * markup have finished updating, without touching the cart's own events.
     */
    queueRender() {
      if (this.#renderQueued) return;
      this.#renderQueued = true;

      queueMicrotask(() => {
        this.#renderQueued = false;
        if (this.isConnected) this.render();
      });
    }

    resolveThreshold() {
      const sourceCurrency = normaliseCurrency(this.dataset.awSourceCurrency, 'EUR');
      const storeCurrency = normaliseCurrency(this.dataset.awStoreCurrency, sourceCurrency);
      const activeCurrency = normaliseCurrency(this.dataset.awCurrency, sourceCurrency);
      const baseThreshold = toFiniteNumber(this.dataset.awBaseThreshold) ?? 49;
      const fallbackThreshold = toFiniteNumber(this.dataset.awFallbackThreshold) ?? baseThreshold;

      if (activeCurrency === sourceCurrency) {
        return Math.ceil(baseThreshold - EPSILON);
      }

      const storefrontCurrency = window.Shopify?.currency;
      const storefrontActiveCurrency = normaliseCurrency(storefrontCurrency?.active, activeCurrency);
      const storefrontRate = toFiniteNumber(storefrontCurrency?.rate);

      if (
        sourceCurrency === storeCurrency &&
        storefrontActiveCurrency === activeCurrency &&
        storefrontRate !== null &&
        storefrontRate > 0
      ) {
        return Math.ceil(baseThreshold * storefrontRate - EPSILON);
      }

      return Math.ceil(fallbackThreshold - EPSILON);
    }

    render() {
      const amountNode = this.querySelector('[data-aw-shipping-amount]');
      if (!amountNode) return;

      const locale = this.dataset.awLocale || document.documentElement.lang || 'de';
      const currency = normaliseCurrency(this.dataset.awCurrency, 'EUR');
      const threshold = this.resolveThreshold();
      const mode = this.dataset.awMode || 'threshold';

      this.dataset.awResolvedThreshold = String(threshold);

      if (mode === 'progress') {
        const cartTotal = Math.max(0, toFiniteNumber(this.dataset.awCartTotal) ?? 0);
        const precision = getCurrencyPrecision(locale, currency);
        const scale = 10 ** precision;
        const amountRemaining = Math.max(0, Math.ceil((threshold - cartTotal - EPSILON) * scale) / scale);
        const unlocked = amountRemaining <= EPSILON;
        const progress = threshold > 0 ? Math.min(100, Math.max(0, (cartTotal / threshold) * 100)) : 100;
        const progressCopy = this.querySelector('[data-aw-shipping-progress-copy]');
        const unlockedCopy = this.querySelector('[data-aw-shipping-unlocked-copy]');
        const fill = this.querySelector('.aw-cart-shipping__fill');

        amountNode.textContent = formatCurrency(amountRemaining, locale, currency, false);
        this.classList.toggle('aw-cart-shipping--unlocked', unlocked);
        this.dataset.awCartShippingProgress = progress.toFixed(2);

        if (progressCopy) progressCopy.hidden = unlocked;
        if (unlockedCopy) unlockedCopy.hidden = !unlocked;
        if (fill) fill.style.setProperty('--aw-cart-shipping-progress', `${progress.toFixed(2)}%`);
      } else {
        amountNode.textContent = formatCurrency(threshold, locale, currency, true);
      }

      this.dataset.awReady = 'true';
    }
  }

  customElements.define(ELEMENT_NAME, AwShippingThreshold);
})();
